import React from 'react'
import {
  Box,
  Typography,
  Chip,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { eInventoryCheckState } from '../../../enums/eInventoryCheck'

export interface InventoryCheckCardItem {
  inventoryCheckId: number
  stateId: number
  totalPosition: number
  qtyCycles: number
  tag?: string
  promiseDate?: string
  progress?: number
  warehouseName?: string
}

export const getInventoryCheckStateName = (stateId: number): string => {
  if (stateId === eInventoryCheckState['Creada']) return 'Pendiente'
  if (stateId === eInventoryCheckState['En Proceso']) return 'En proceso'
  if (stateId === eInventoryCheckState['Finalizada']) return 'Finalizada'
  if (stateId === eInventoryCheckState['Cancelada']) return 'Cancelada'
  return eInventoryCheckState[stateId] ?? 'Desconocido'
}

interface InventoryCheckCardProps {
  item: InventoryCheckCardItem
  index: number
  isExpanded: boolean
  onToggle: () => void
  onSelect: () => void
  disabled?: boolean
}

export const InventoryCheckCard: React.FC<InventoryCheckCardProps> = ({
  item,
  index,
  isExpanded,
  onToggle,
  onSelect,
  disabled = false
}) => {
  const stateName = getInventoryCheckStateName(item.stateId)
  const isEnProceso = item.stateId === eInventoryCheckState['En Proceso']
  const label = item.tag || `Conteo: ${item.inventoryCheckId}`

  const chipStyle: React.CSSProperties = {
    backgroundColor: isEnProceso ? '#dbeafe' : '#f0f0f0',
    color: isEnProceso ? '#1d4ed8' : '#666',
    fontWeight: 600,
    height: 22
  }

  return (
    <Box
      style={{
        position: 'relative',
        width: '100%',
        marginBottom: 10
      }}
    >
      {/* Acordeón — deja 13% al botón siempre */}
      <Box style={{ width: '90%', transition: 'width 0.2s' }}>
        <Accordion
          expanded={isExpanded}
          onChange={onToggle}
          disableGutters
          elevation={0}
          sx={{
            borderRadius: '12px 0px 0px 12px !important',
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary
            sx={{
              backgroundColor: '#e2e8f0',
              minHeight: '48px',
              '& .MuiAccordionSummary-content': { margin: '8px 0' }
            }}
          >
            <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Typography
                variant='body2'
                style={{ color: '#999', fontWeight: 500, minWidth: 16 }}
              >
                {index + 1}
              </Typography>
              <Box>
                <Typography
                  variant='body2'
                  style={{ fontWeight: 700, marginBottom: 4 }}
                >
                  {label}
                </Typography>
                <Chip label={stateName} size='small' style={chipStyle} />
              </Box>
            </Box>
          </AccordionSummary>

          {/* onClick en los detalles permite contraer tocando el contenido */}
          <AccordionDetails
            onClick={onToggle}
            sx={{ backgroundColor: '#fff', padding: '12px 16px', cursor: 'pointer' }}
          >
            <Divider style={{ marginBottom: 8 }} />
            {item.warehouseName && (
              <Typography variant='caption' display='block'>
                <b>Almacén:</b> {item.warehouseName}
              </Typography>
            )}
            <Typography variant='caption' display='block'>
              <b>Posiciones:</b> {item.totalPosition / item.qtyCycles}
            </Typography>
            {item.promiseDate && (
              <Typography variant='caption' display='block'>
                <b>Vence:</b>{' '}
                {new Date(item.promiseDate).toLocaleDateString('es-CL')}
              </Typography>
            )}
            {item.progress !== undefined && (
              <Box style={{ marginTop: 4 }}>
                <Chip
                  label={`${Math.min(100, Math.round(item.progress ?? 0))}%`}
                  size='small'
                  style={{
                    backgroundColor: '#dbeafe',
                    color: '#1d4ed8',
                    fontWeight: 600,
                    height: 22
                  }}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Botón siempre visible: deshabilitado cuando está comprimido */}
      <Box
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '10%',
          height: '100%'
        }}
      >
        <Button
          variant='contained'
          color='primary'
          disabled={!isExpanded || disabled}
          onClick={() => isExpanded && !disabled && onSelect()}
          disableElevation
          style={{
            height: '100%',
            minWidth: 0,
            width: '100%',
            borderRadius: '0px 12px 12px 0px'
          }}
        >
          <ChevronRightIcon />
        </Button>
      </Box>
    </Box>
  )
}
